import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import type { OnIdentifyEffects } from '@ngrx/effects';
import {
  catchError,
  EMPTY,
  from,
  map,
  of,
  switchMap,
  tap,
  filter,
  exhaustMap,
} from 'rxjs';
import { ToastService } from 'mt-ui-lib/services/toast';
import { TranslateService } from '@ngx-translate/core';
import { withLatestFrom } from 'rxjs/operators';
import { IAppState } from '@app/app.state';
import { Store } from '@ngrx/store';
import { Dialog } from '@angular/cdk/dialog';
import { openConfirmDialog } from 'mt-ui-lib/components/dialog';
import { SystemParamsActions } from './system-params.actions';
import { SystemParamsGridService } from '../system-params-grid/system-params-grid.service';
import {
  selectParamHasChanges,
  selectParamIsLoading,
  selectParamSource,
} from './system-params.selectors';
import { SYSTEM_PARAMS_DATA_SOURCE } from '@core/api/system-params-data-source';
import { SystemParamsHistoryGridService } from '../system-params-card/ag-system-params-history-grid/system-params-history-grid.service';
import { parseError } from 'mt-ui-lib/functions';

@Injectable()
export class SystemParamsEffects implements OnIdentifyEffects {
  protected readonly dialog = inject(Dialog);
  protected readonly systemParamsGridService = inject(SystemParamsGridService);
  protected readonly systemParamsHistoryGridService = inject(
    SystemParamsHistoryGridService,
  );
  private readonly store = inject(Store<IAppState>);
  private readonly systemParamsApiService = inject(SYSTEM_PARAMS_DATA_SOURCE);
  private readonly toastrService = inject(ToastService);
  private readonly translateService = inject(TranslateService);
  private readonly actions$ = inject(Actions);
  private readonly activeSource = this.store.selectSignal(selectParamSource);

  public ngrxOnIdentifyEffects(): string {
    return this.systemParamsApiService.source;
  }

  attemptSelectParam$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SystemParamsActions.attemptSelectParam),
      filter((action) => this.accepts(action.source)),
      filter(() => !this.store.selectSignal(selectParamIsLoading)()),
      withLatestFrom(this.store.select(selectParamHasChanges)),
      switchMap(([action, hasChanges]) => {
        if (!action?.id) {
          return of(SystemParamsActions.closeParam({ source: action.source }));
        }

        if (!hasChanges) {
          return of(
            SystemParamsActions.selectParam({
              source: action.source,
              id: action.id,
            }),
          );
        }

        const ref = openConfirmDialog(this.dialog);

        return from(ref.closed).pipe(
          switchMap((res) => {
            if (!res) {
              return EMPTY;
            }

            return of(
              SystemParamsActions.selectParam({
                source: action.source,
                id: action.id,
              }),
            );
          }),
        );
      }),
    ),
  );

  closeParam$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SystemParamsActions.closeParam),
      filter((action) => this.accepts(action.source)),
      filter(() => !this.store.selectSignal(selectParamIsLoading)()),
      withLatestFrom(this.store.select(selectParamHasChanges)),
      switchMap(([action, hasChanges]) => {
        if (!hasChanges) {
          return of(SystemParamsActions.clearParam({ source: action.source }));
        }

        const ref = openConfirmDialog(this.dialog);

        return from(ref.closed).pipe(
          switchMap((res) => {
            if (!res) {
              return EMPTY;
            }

            return of(
              SystemParamsActions.clearParam({ source: action.source }),
            );
          }),
        );
      }),
    ),
  );

  selectParam$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SystemParamsActions.selectParam),
      filter((action) => this.accepts(action.source)),
      switchMap((action) => {
        if (typeof action?.id === 'string') {
          return of(
            SystemParamsActions.requestParamInfo({
              source: action.source,
              id: action.id,
            }),
          );
        }

        return EMPTY;
      }),
    ),
  );

  requestParamInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SystemParamsActions.requestParamInfo),
      filter((action) => this.accepts(action.source)),
      switchMap((action) =>
        this.systemParamsApiService.getInfo(action.id).pipe(
          map((param) =>
            SystemParamsActions.requestParamInfoSuccess({
              source: action.source,
              param,
            }),
          ),
          tap(() => this.systemParamsHistoryGridService.refreshGrid()),
          catchError((error) =>
            of(
              SystemParamsActions.requestParamInfoError({
                source: action.source,
                error,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  requestUpdateParam$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SystemParamsActions.requestUpdateParam),
      filter((action) => this.accepts(action.source)),
      exhaustMap((action) =>
        this.systemParamsApiService.updateParam(action.id, action.params).pipe(
          map(() =>
            SystemParamsActions.requestUpdateParamSuccess({
              source: action.source,
              id: action.id,
            }),
          ),
          catchError((error) =>
            of(
              SystemParamsActions.requestUpdateParamError({
                source: action.source,
                error,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  requestUpdateParamSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SystemParamsActions.requestUpdateParamSuccess),
      filter((action) => this.accepts(action.source)),
      switchMap((action) => {
        this.systemParamsGridService.refreshGrid();

        return of(
          SystemParamsActions.requestParamInfo({
            source: action.source,
            id: action.id,
          }),
        );
      }),
    ),
  );

  showSucces$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SystemParamsActions.requestUpdateParamSuccess),
        filter((action) => this.accepts(action.source)),
        tap((action) => {
          let successTitle: string = null;

          switch (action.type) {
            case '[SYSTEM PARAMS ADMIN] requestUpdateParamSuccess':
              successTitle = this.translateService.instant(
                'ADMIN.SYSTEM_PARAMS.CARD.FUNCTIONS.UPDATE.SUCCESS',
              );
              break;
            default:
              successTitle = this.translateService.instant(
                'GENERAL.SUCCESS_SAVE',
              );
          }

          this.toastrService.add({ level: 'success', summary: successTitle });
        }),
      ),
    { dispatch: false },
  );

  showErrors$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          SystemParamsActions.requestUpdateParamError,
          SystemParamsActions.requestParamInfoError,
        ),
        filter((action) => this.accepts(action.source)),
        tap((action) => {
          let errorTitle: string = null;

          switch (action.type) {
            case '[SYSTEM PARAMS ADMIN] requestUpdateParamError':
              errorTitle = this.translateService.instant(
                'ADMIN.SYSTEM_PARAMS.CARD.FUNCTIONS.UPDATE.ERROR',
              );
              break;
            default:
              errorTitle = this.translateService.instant(
                'GENERAL.ERROR_REQUEST',
              );
              break;
          }

          this.toastrService.add({
            level: 'error',
            summary: errorTitle,
            detail: parseError(action.error),
          });
        }),
      ),
    { dispatch: false },
  );

  private accepts(source: string): boolean {
    return (
      source === this.systemParamsApiService.source &&
      source === this.activeSource()
    );
  }
}
